import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ReviewItems from '../components/ReviewItems';
import McpApprovals from './McpApprovals';

const Section = ({ id, title, count, children }) => (
  <section id={id} style={{ marginBottom: 24 }}>
    <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>
      {title} {count > 0 && <span className="badge badge-waiting_approval" style={{ fontSize: 11 }}>{count}</span>}
    </h3>
    {children}
  </section>
);

function BenefitsToApprove({ transcriptionId, onChange }) {
  const { api } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState(null);

  const load = useCallback(() => api(`/transcriptions/${transcriptionId}/benefits`)
    .then(r => setItems((r.items || []).filter(b => b.status === 'suggested')))
    .catch(err => toast.error(err.message)), [api, transcriptionId, toast]);
  useEffect(() => { load(); }, [load]);

  const approve = async (b) => {
    try {
      await api(`/transcriptions/${transcriptionId}/benefits/${b.id}`, { method: 'PUT', body: JSON.stringify({ status: 'approved' }) });
      load();
      if (onChange) onChange();
    } catch (err) { toast.error(err.message); }
  };

  if (!items) return null;
  return items.map(b => (
    <div key={b.id} style={{ borderTop: '1px solid var(--border)', padding: '8px 0', fontSize: 13 }}>
      <div style={{ fontWeight: 600 }}>{b.title}</div>
      <div style={{ color: 'var(--text-muted)', margin: '4px 0' }}>{b.text}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="btn btn-sm btn-primary" onClick={() => approve(b)}>اعتماد الإرسال</button>
        <Link className="btn btn-sm btn-outline" to={`/transcription?id=${transcriptionId}${b.start_seconds != null ? `&t=${Math.floor(b.start_seconds)}` : ''}`}>تعديل / سماع</Link>
      </div>
    </div>
  ));
}

/**
 * صندوق الاعتماد — عمل المدير الوحيد بعد أن صار Claude يضبط التفريغ ويستخرج
 * الفوائد: تفريغات للنشر بتقرير ضبطها، ومواضع تحتاج سماعه، وفوائد للإرسال،
 * وطلبات موافقة الموصل (ردود العملاء والملاحظات الشهرية والتقارير والمال).
 */
export default function ApprovalsInbox() {
  const { api } = useAuth();
  const toast = useToast();
  const [inbox, setInbox] = useState(null);
  const [openListen, setOpenListen] = useState(null);
  const [openBenefits, setOpenBenefits] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api('/approvals/inbox').then(setInbox).catch(() => setInbox({ transcriptions: [], listening: [], benefits: [], counts: {} })), [api]);
  useEffect(() => { load(); }, [load]);

  const showListening = (id) => {
    setOpenListen(id);
    document.getElementById('listening')?.scrollIntoView({ behavior: 'smooth' });
  };

  const decide = async (tr, action) => {
    let body = {};
    if (action === 'return') {
      const note = window.prompt('ملاحظة الإرجاع (تصل إلى من يضبط التفريغ):');
      if (!note) return;
      body = { note };
    } else if (!window.confirm(`اعتماد «${tr.title}» للنشر باسم ${tr.sheikh || 'الشيخ'}؟`)) {
      return;
    }
    setBusy(tr.id);
    try {
      const r = await api(`/transcriptions/${tr.id}/approval/${action}`, { method: 'POST', body: JSON.stringify(body) });
      toast.success(action === 'approve' ? (r.published_url ? 'اعتُمد ونُشر' : 'اعتُمد') : 'أُرجع بملاحظة');
      load();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  if (!inbox) return <div className="loader">جاري التحميل...</div>;
  const c = inbox.counts || {};

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginBottom: 4 }}>صندوق الاعتماد</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
        كل ما ينتظر قرارك في مكان واحد: {c.transcriptions || 0} تفريغ، {c.listening || 0} موضع للسماع، {c.benefits || 0} فائدة، {c.mcp || 0} طلب موافقة.
      </p>

      <Section title="تفريغات جاهزة للنشر" count={c.transcriptions}>
        {inbox.transcriptions.length === 0 && <div className="card" style={{ color: 'var(--text-muted)' }}>لا شيء ينتظر.</div>}
        {inbox.transcriptions.map(tr => (
          <div key={tr.id} className="card" style={{ marginBottom: 10 }} data-testid="ready-transcription">
            <div style={{ fontWeight: 600 }}>#{tr.id} — {tr.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0' }}>{tr.sheikh || 'غير مربوط بشيخ'}</div>
            <div style={{ fontSize: 13, lineHeight: 1.9 }}>
              تصحيحات: {tr.report.corrections} (منها {tr.report.corrections_by_claude} من Claude) ·
              حُسم آلياً: {tr.report.resolved_auto} · حسمه Claude: {tr.report.resolved_claude} · حسمته: {tr.report.resolved_human}
              <br />
              {tr.report.words_match ? '✓ ألفاظ المنسّق مطابقة للخام' : '✗ المنسّق يخالف الخام في ألفاظه'}
              {tr.report.open > 0 && <span style={{ color: 'var(--c-coral)' }}> · {tr.report.open} بنداً معلّقاً</span>}
              {tr.report.listening > 0 && (
                <>
                  <br />
                  <button className="btn btn-link" style={{ padding: 0, color: 'var(--c-coral)' }} onClick={() => showListening(tr.id)}>
                    {tr.report.listening} موضعاً ينتظر سماعك — يُعتمد بعد حسمها
                  </button>
                </>
              )}
            </div>
            {tr.note && <div style={{ fontSize: 13, marginTop: 6 }}>💬 {tr.note}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy === tr.id || tr.report.open > 0 || tr.report.listening > 0} onClick={() => decide(tr, 'approve')}>اعتماد</button>
              <button className="btn btn-outline" disabled={busy === tr.id} onClick={() => decide(tr, 'return')}>إرجاع بملاحظة</button>
              <Link className="btn btn-outline" to={`/transcription?id=${tr.id}`}>🔍 الفرق عن الخام</Link>
            </div>
          </div>
        ))}
      </Section>

      <Section id="listening" title="مواضع تحتاج سماعاً" count={c.listening}>
        {inbox.listening.length === 0 && <div className="card" style={{ color: 'var(--text-muted)' }}>لا شيء ينتظر.</div>}
        {inbox.listening.map(g => (
          <div key={g.transcription_id} className="card" style={{ marginBottom: 10 }}>
            <button className="btn btn-link" style={{ padding: 0, fontWeight: 600 }} onClick={() => setOpenListen(o => (o === g.transcription_id ? null : g.transcription_id))}>
              {g.title} — {g.count} موضعاً
            </button>
            {openListen === g.transcription_id && (
              <div style={{ marginTop: 8 }}>
                <ReviewItems transcriptionId={g.transcription_id} status="needs_listening" onChange={load} />
              </div>
            )}
          </div>
        ))}
      </Section>

      <Section title="فوائد للإرسال إلى الشيخ" count={c.benefits}>
        {inbox.benefits.length === 0 && <div className="card" style={{ color: 'var(--text-muted)' }}>لا شيء ينتظر.</div>}
        {inbox.benefits.map(g => (
          <div key={g.transcription_id} className="card" style={{ marginBottom: 10 }}>
            <button className="btn btn-link" style={{ padding: 0, fontWeight: 600 }} onClick={() => setOpenBenefits(o => (o === g.transcription_id ? null : g.transcription_id))}>
              {g.title}{g.sheikh ? ` (${g.sheikh})` : ''} — {g.count} فائدة
            </button>
            {openBenefits === g.transcription_id && <BenefitsToApprove transcriptionId={g.transcription_id} onChange={load} />}
          </div>
        ))}
      </Section>

      <Section title="ردود العملاء، وملاحظات الشهر والتقارير، وبقيّة طلبات الموصل" count={c.mcp}>
        <McpApprovals />
      </Section>

      {(inbox.auto_executed || []).length > 0 && (
        <Section title="نُفّذ آلياً (آخر سبعة أيام)">
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0 }}>
            أدوات داخلية في قائمة التنفيذ الآلي (الإعدادات): للعلم، لا تنتظر قراراً.
          </p>
          {inbox.auto_executed.map(row => (
            <div key={row.id} className="card" style={{ marginBottom: 6, padding: '8px 12px', fontSize: 13 }} data-testid="auto-executed">
              <span className="badge badge-info" style={{ marginInlineEnd: 6 }}>{row.label}</span>
              {row.title}
              {row.transcription_id && <span> · تفريغ #{row.transcription_id}</span>}
              {row.audio_seconds > 0 && <span> · نحو {Math.round(row.audio_seconds / 60)} دقيقة صوت</span>}
              {!row.ok && <span style={{ color: 'var(--c-coral)' }}> · فشل ({row.result_status})</span>}
              <span style={{ float: 'left', color: 'var(--text-faint)', fontSize: 11 }}>{new Date(row.at).toLocaleString('ar-EG')}</span>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}
